﻿import React, { useState, useContext, useMemo } from 'react';
import Context, { context_type } from '@Util/Context';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { str_isEmpty, site_should_show_last_exception_warning, site_should_show_last_response_time_warning } from '@Util/Utils';
import { dashboard_pages } from '@Util/GlobalValues';

import Table_Head from '@Elements/Table/Table_Head';
import Table_Data from '@Elements/Table/Table_Data';
import { Tooltip } from 'react-tooltip';
import dayjs from 'dayjs';

import { Client_Website_Type } from '@Util/Types';

export default function Client_Websites_Table(): JSX.Element {
    const { client_websites }: context_type = useContext(Context);
    const navigate = useNavigate();
    const [t] = useTranslation();

    const [sort_direction, set_sort_direction] = useState<"Ascending" | "Descending">("Ascending");
    const [active_sorting_column, set_active_sorting_column] = useState<string>("name");

    const table_data: Client_Website_Type[] = useMemo(() => {
        let sorted_data: Client_Website_Type[] = [];

        if (sort_direction == "Ascending") {
            sorted_data = client_websites.sort((a: Client_Website_Type, b: Client_Website_Type) => a[active_sorting_column] > b[active_sorting_column] ? 1 : -1);
        } else {
            sorted_data = client_websites.sort((a: Client_Website_Type, b: Client_Website_Type) => a[active_sorting_column] < b[active_sorting_column] ? 1 : -1);
        }

        return sorted_data;
    }, [client_websites, sort_direction, active_sorting_column]);

    function handleSetSortingOnTableHead(col: string): void {
        if (str_isEmpty(col)) {
            throw new Error("Handle sorting on table: Column is empty");
        }

        if (col === active_sorting_column) {
            if (sort_direction == "Ascending") {
                return set_sort_direction("Descending");
            } else {
                return set_sort_direction("Ascending");
            }
        }

        set_active_sorting_column(col);
        set_sort_direction("Ascending");
    }

    function handle_row_click(site: Client_Website_Type): void {
        navigate(dashboard_pages.customer_websites + "/" + site.id);
    }

    function handle_last_response_time_column(site: Client_Website_Type): JSX.Element {
        let show_warning: boolean = site_should_show_last_response_time_warning(site);

        return (
            <Table_Data
                align="right"
                style={{ width: "20%" }}
                className={show_warning ? "Table_Data_Warning" : ""}
            >
                <a
                    data-tooltip-id={`${site.id}_lastResponseTime_tooltip`}
                    data-tooltip-content={`${t('Text.AverageResponseTime')}: ${site.averageResponseTime.toFixed(1)} ms`}
                    data-tooltip-place="top"
                >
                    {
                        show_warning &&
                        <svg xmlns="http://www.w3.org/2000/svg" height="48" viewBox="0 -960 960 960" width="48"><path d="m40-120 440-760 440 760H40Zm104-60h672L480-760 144-180Zm340.175-57q12.825 0 21.325-8.675 8.5-8.676 8.5-21.5 0-12.825-8.675-21.325-8.676-8.5-21.5-8.5-12.825 0-21.325 8.675-8.5 8.676-8.5 21.5 0 12.825 8.675 21.325 8.676 8.5 21.5 8.5ZM454-348h60v-224h-60v224Zm26-122Z" /></svg>
                    }
                    {`${site.lastResponseTime.toFixed(1)} ms`}
                </a>
                <Tooltip id={`${site.id}_lastResponseTime_tooltip`} />
            </Table_Data>
        )
    }

    function handle_last_exception_count_column(site: Client_Website_Type): JSX.Element {
        const show_warning: boolean = site_should_show_last_exception_warning(site);

        return (
            <Table_Data
                align="right"
                style={{ width: "20%" }}
                className={show_warning ? "Table_Data_Warning" : ""}
            >
                <a
                    data-tooltip-id={`${site.id}_exceptionCount_tooltip`}
                    data-tooltip-content={`${t('Text.AverageExceptions')}: ${site.averageExceptionCount.toFixed(1)} ${t('Text.Pcs')}`}
                    data-tooltip-place="top"
                >
                    {
                        show_warning &&
                        <svg xmlns="http://www.w3.org/2000/svg" height="48" viewBox="0 -960 960 960" width="48"><path d="m40-120 440-760 440 760H40Zm104-60h672L480-760 144-180Zm340.175-57q12.825 0 21.325-8.675 8.5-8.676 8.5-21.5 0-12.825-8.675-21.325-8.676-8.5-21.5-8.5-12.825 0-21.325 8.675-8.5 8.676-8.5 21.5 0 12.825 8.675 21.325 8.676 8.5 21.5 8.5ZM454-348h60v-224h-60v224Zm26-122Z" /></svg>
                    }
                    {`${site.lastExceptionCount} ${t('Text.Pcs')}`}
                </a>
                <Tooltip id={`${site.id}_exceptionCount_tooltip`} />
            </Table_Data>
        )
    }

    function Empty_Table(): JSX.Element {
        return (
            <tr>
                <Table_Data align="left">
                    <span className="Table_Data_Customer_Name">{t('Text.NoClientWebsitesToShow')}</span>
                </Table_Data>
                <Table_Data align="right">
                    <></>
                </Table_Data>
            </tr>
        )
    }

    return (
        <>
            <div className="Table_Wrapper">
                <table className="Table Table--Anchored">
                    <thead>
                        <tr>
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Customer')}
                                sortingAction={() => handleSetSortingOnTableHead('name')}
                                sortingDataName="name"
                                style={{ width: "40%" }}
                            />
                            <Table_Head
                                align="right"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.ResponseTime')}
                                sortingAction={() => handleSetSortingOnTableHead('lastResponseTime')}
                                sortingDataName="lastResponseTime"
                                style={{ width: "20%" }}
                            />
                            <Table_Head
                                align="right"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Exceptions')}
                                sortingAction={() => handleSetSortingOnTableHead('lastExceptionCount')}
                                sortingDataName="lastExceptionCount"
                                style={{ width: "20%" }}
                            />
                            <Table_Head
                                align="right"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.LastUpdated')}
                                sortingAction={() => handleSetSortingOnTableHead('lastUpdated')}
                                sortingDataName="lastUpdated"
                                style={{ width: "20%" }}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {
                            table_data.isEmpty() ?
                                <Empty_Table /> :
                                table_data.map((site: Client_Website_Type, index: number) =>
                                    <tr key={`customer_website_table_row${index}`} onClick={() => handle_row_click(site)}>
                                        <Table_Data
                                            align="left"
                                            style={{ width: "40%" }}
                                        >
                                            {site.name}
                                        </Table_Data>
                                        {handle_last_response_time_column(site)}
                                        {handle_last_exception_count_column(site)}
                                        <Table_Data
                                            align="right"
                                            style={{ width: "20%" }}
                                        >
                                            {dayjs(site.lastUpdated).format("YYYY-MM-DD - HH:mm")}
                                        </Table_Data>
                                    </tr>
                                )
                        }
                    </tbody>
                </table>
            </div>
        </>
    );
}